

'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import Image from '@/components/ui/Image';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Shield, Users, Crown, Calendar, Info, ShieldCheck, Hourglass, UserPlus, Trash2, Check, X, Newspaper, ImageIcon, Edit, Copy, Send, Link as LinkIcon, Handshake, Heart, Palette, Church, FileSignature, AlignLeft, Star, Pin, Settings, PlusCircle, BookOpen, Mail, FileText, Music, Route, Undo2, Megaphone, Clock, Vote } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { requestToJoinHermandad, approveJoinRequest, rejectJoinRequest, removeBrother, createSocialPost, deleteSocialPost, updateSocialPost, createSocialStory, inviteToCollaborate, acceptCollaboration, rejectCollaboration, toggleLikePost, saveHermandadSettings, createContract, signContract, rejectContract, deleteSocialStory, getArchivedStories, createHighlightStoryGroup, deleteHighlightStoryGroup, sendCarta, getPapeletasByHermandad, deletePapeleta, getDiasSS, getProgramaSSConfig, getCityMapConfig, submitRecorrido, submitHorario, sendServerAnnouncement } from '@/lib/actions';
import { MediaUploader } from '@/components/social/MediaUploader';
import { SongPicker } from '@/components/social/SongPicker';
import { MentionTextarea } from '@/components/social/MentionTextarea';
import { RouteMapEditor, isNear, CARRERA_OFICIAL_COLOR, darkenColor } from '@/components/programa-ss/RouteMapEditor';
import { RecorridoEditor, type RecorridoDraft } from '@/components/programa-ss/RecorridoEditor';
import { CuadroHorariosEditor, CuadroHorariosTabla, type EstadoCuadro, type ItinerarioRecord } from '@/components/programa-ss/CuadroHorariosEditor';
import type { DiaSS, DiaSSHermandadDetalles, HorarioSolicitud, RecorridoPunto, RecorridoSolicitud } from '@/lib/types';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { JartinetHermandad, UserProfile, JoinRequest, SocialPost, SocialStory, Banda, Contract, Organizacion, HighlightStoryGroup, PapeletaDeSitio } from "@/lib/types";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '@/lib/utils';
import { useMemoFirebase, useAuth } from '@/firebase/index';
import { db as firestore } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { useDoc } from '@/firebase/firestore/use-doc';
import { ContractCard } from '../contratos/ContractCard';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { doc, collection, query, where, orderBy } from 'firebase/firestore';
import { Checkbox } from '../ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PrimitivaIcon } from '@/components/icons/PrimitivaIcon';
import { PontificiaIcon } from '@/components/icons/PontificiaIcon';
import { PapeletaDeSitioPreview, PapeletaDeSitioDoc } from './PapeletaDeSitio';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CreatePapeletaForm } from './CreatePapeletaForm';
import { VotacionesSection } from './VotacionesSection';


function toDateSafe(v: any): Date {
  if (!v) return new Date(0);
  if (typeof v === "object" && typeof v.toDate === "function") return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? new Date(0) : d;
}

function SendCartaForm({ hermandadId, onFormSuccess }: { hermandadId: string, onFormSuccess: () => void }) {
    const { toast } = useToast();
    const formRef = useRef<HTMLFormElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData(event.currentTarget);
        const result = await sendCarta(hermandadId, formData);
        
        if (result.success) {
            toast({ title: "Éxito", description: result.message });
            formRef.current?.reset();
            onFormSuccess();
        } else {
            toast({ title: "Error", description: result.message, variant: "destructive" });
        }
        setIsSubmitting(false);
    };

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="subject">Asunto</Label>
                <Input id="subject" name="subject" required placeholder="Ej: Citación para Cultos, Convocatoria de Cabildo..." />
            </div>
            <div className="space-y-2">
                <Label htmlFor="content">Contenido de la Carta</Label>
                <RichTextarea id="content" name="content" required rows={10} placeholder="Escribe aquí el cuerpo de la carta. Puedes usar Markdown." />
                <p className="text-xs text-muted-foreground">Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link> para dar formato.</p>
            </div>
            <DialogFooter>
                <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
                <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Enviar a todos los hermanos
                </Button>
            </DialogFooter>
        </form>
    );
}

// Anuncio al servidor principal de Minecraft (bossbar 1 min para todos los jugadores).
function ServerAnnouncementForm({ hermandadId, onFormSuccess }: { hermandadId: string, onFormSuccess: () => void }) {
    const { toast } = useToast();
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!message.trim()) return;
        setIsSubmitting(true);
        const result = await sendServerAnnouncement(hermandadId, message.trim());
        if (result.success) {
            toast({ title: 'Anuncio enviado', description: result.message });
            setMessage('');
            onFormSuccess();
        } else {
            toast({ title: 'Error', description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="announce-msg">Mensaje del anuncio</Label>
                <Input id="announce-msg" value={message} maxLength={200}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Ej: ¡Salida procesional en 10 minutos desde la Catedral!" />
                <p className="text-xs text-muted-foreground">
                    Aparecerá durante <strong>1 minuto</strong> en la pantalla de todos los jugadores del
                    servidor principal, con el nombre de la hermandad encima.
                </p>
            </div>
            <DialogFooter>
                <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
                <Button onClick={handleSubmit} disabled={isSubmitting || !message.trim()}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Enviar al servidor
                </Button>
            </DialogFooter>
        </div>
    );
}

function CreateContractForm({ emitter, allHermandades, allBandas, allOrganizaciones = [], onFormSuccess }: { emitter: {id: string, type: string, name: string}, allHermandades: JartinetHermandad[], allBandas: Banda[], allOrganizaciones?: Organizacion[], onFormSuccess: () => void }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const result = await createContract(formData);
    
    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 p-1">
      <input type="hidden" name="emitterId" value={`${emitter.type}:${emitter.id}`} />
      <div className="space-y-2">
        <Label htmlFor="concept">Concepto</Label>
        <Input id="concept" name="concept" placeholder="Ej: Acompañamiento Musical Domingo de Ramos" />
      </div>
       <div className="space-y-2">
        <Label htmlFor="recipientId">Destinatario</Label>
         <Select name="recipientId">
            <SelectTrigger><SelectValue placeholder="Elige un perfil..." /></SelectTrigger>
            <SelectContent>
                {allHermandades.filter(h => h.id !== emitter.id).map(h => (
                    <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                        {h.nombreCompleto} (Hermandad)
                      </div>
                    </SelectItem>
                ))}
                {allBandas.filter(b => b.id !== emitter.id).map(b => (
                    <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                      <div className="flex items-center gap-2">
                         <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                         {b.nombreCompleto} (Banda)
                      </div>
                    </SelectItem>
                ))}
                {allOrganizaciones.filter(o => o.id !== emitter.id).map(o => (
                    <SelectItem key={`organizacion:${o.id}`} value={`organizacion:${o.id}`}>
                      <div className="flex items-center gap-2">
                         <Avatar className="h-5 w-5"><AvatarImage src={o.escudoUrl} /></Avatar>
                         {o.nombreCompleto} (Organización)
                      </div>
                    </SelectItem>
                ))}
            </SelectContent>
         </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="content">Contenido del Contrato</Label>
        <RichTextarea id="content" name="content" placeholder="Escribe aquí las cláusulas del contrato..." rows={10} />
         <p className="text-xs text-muted-foreground">Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link> para dar formato (negrita, cursiva...)</p>
      </div>
      <DialogFooter>
        <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Enviar Contrato
        </Button>
      </DialogFooter>
    </form>
  )
}

function InviteCollaborationForm({ postId, allHermandades, allBandas, allOrganizaciones, currentAuthor, onFormSuccess }: { postId: string, allHermandades: JartinetHermandad[], allBandas: Banda[], allOrganizaciones: Organizacion[], currentAuthor: { type: 'hermandad' | 'banda' | 'organizacion', id: string }, onFormSuccess: () => void }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const result = await inviteToCollaborate(formData);

    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };
  
  const otherHermandades = allHermandades.filter(h => !(currentAuthor.type === 'hermandad' && h.id === currentAuthor.id));
  const otherBandas = allBandas.filter(b => !(currentAuthor.type === 'banda' && b.id === currentAuthor.id));
  const otherOrganizaciones = allOrganizaciones.filter(o => !(currentAuthor.type === 'organizacion' && o.id === currentAuthor.id));


  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <input type="hidden" name="postId" value={postId} />
      <div className="space-y-2">
        <Label htmlFor="collaboratorId">Selecciona un perfil social para invitar</Label>
        <Select name="collaboratorId" required>
            <SelectTrigger>
                <SelectValue placeholder="Elige un perfil..." />
            </SelectTrigger>
            <SelectContent>
                {otherHermandades.map(h => (
                    <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                        <div className="flex items-center gap-2">
                            <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                            {h.nombreCompleto} (Hermandad)
                        </div>
                    </SelectItem>
                ))}
                 {otherBandas.map(b => (
                    <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                        <div className="flex items-center gap-2">
                            <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                            {b.nombreCompleto} (Banda)
                        </div>
                    </SelectItem>
                ))}
                 {otherOrganizaciones.map(o => (
                    <SelectItem key={`organizacion:${o.id}`} value={`organizacion:${o.id}`}>
                        <div className="flex items-center gap-2">
                            <Avatar className="h-5 w-5"><AvatarImage src={o.escudoUrl} /></Avatar>
                            {o.nombreCompleto} (Organización)
                        </div>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Enviar Invitación
        </Button>
      </DialogFooter>
    </form>
  )
}

function CreateStoryForm({ entityId, entityType, onFormSuccess }: { entityId: string, entityType: 'hermandad' | 'banda' | 'organizacion', onFormSuccess: () => void }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const result = await createSocialStory(entityId, entityType, formData);
    
    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };
  
  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <MediaUploader single required label="Foto de la historia" />
       <div className="space-y-2">
        <Label htmlFor="text">Texto (Opcional)</Label>
        <RichTextarea id="text" name="text" placeholder="Añade un texto a tu historia..." />
      </div>
      <SongPicker />
      <DialogFooter>
        <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Publicar Historia
        </Button>
      </DialogFooter>
    </form>
  );
}

function CreatePostForm({ hermandadId, onFormSuccess, allHermandades, allBandas, allOrganizaciones }: { hermandadId: string, onFormSuccess: () => void, allHermandades: JartinetHermandad[], allBandas: Banda[], allOrganizaciones: Organizacion[] }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const otherHermandades = allHermandades.filter(h => h.id !== hermandadId);
  
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const result = await createSocialPost(hermandadId, formData);
    
    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };


  return (
    <form id="create-post-form" ref={formRef} onSubmit={handleSubmit} className="space-y-4 p-1">
      <div className="space-y-2">
        <Label htmlFor="title">Título</Label>
        <Input id="title" name="title" placeholder="Título de la publicación" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="content">Contenido</Label>
        <MentionTextarea id="content" name="content" placeholder="Escribe aquí el contenido... usa @ para mencionar" />
         <p className="text-xs text-muted-foreground">Usa <strong>@</strong> para mencionar cuentas. Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link> para dar formato.</p>
      </div>
      <SongPicker />
      <MediaUploader label="Fotos de la publicación" max={10} />
       <div className="space-y-2">
        <Label htmlFor="collaboratorId">Invitar a colaborar (Opcional)</Label>
        <Select name="collaboratorId">
            <SelectTrigger>
                <SelectValue placeholder="Elige un perfil social..." />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="none">No colaborar</SelectItem>
                {otherHermandades.map(h => (
                    <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                        <div className="flex items-center gap-2">
                            <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                            {h.nombreCompleto} (Hermandad)
                        </div>
                    </SelectItem>
                ))}
                {allBandas.map(b => (
                     <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                        <div className="flex items-center gap-2">
                            <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                            {b.nombreCompleto} (Banda)
                        </div>
                    </SelectItem>
                ))}
                 {allOrganizaciones.map(o => (
                    <SelectItem key={`organizacion:${o.id}`} value={`organizacion:${o.id}`}>
                        <div className="flex items-center gap-2">
                            <Avatar className="h-5 w-5"><AvatarImage src={o.escudoUrl} /></Avatar>
                            {o.nombreCompleto} (Organización)
                        </div>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
      </div>
       <DialogFooter className="flex-shrink-0 border-t pt-4">
          <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
          <Button type="submit" disabled={isSubmitting}>
             {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
             Publicar
          </Button>
       </DialogFooter>
    </form>
  )
}

function EditPostForm({ post, onFormSuccess }: { post: SocialPost, onFormSuccess: () => void }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialUrls = Array.isArray(post.mediaUrl) ? post.mediaUrl : (post.mediaUrl ? [post.mediaUrl] : []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const result = await updateSocialPost(post.id, formData);
    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 p-1">
      <div className="space-y-2">
        <Label htmlFor="edit-title">Título</Label>
        <Input id="edit-title" name="title" defaultValue={post.title} placeholder="Título de la publicación" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="edit-content">Contenido</Label>
        <MentionTextarea id="edit-content" name="content" defaultValue={post.content} placeholder="Escribe aquí el contenido... usa @ para mencionar" />
        <p className="text-xs text-muted-foreground">Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link> para dar formato.</p>
      </div>
      <SongPicker
        defaultTrackName={post.spotifyTrackName}
        defaultPreviewUrl={post.audioPreviewUrl}
        defaultStart={post.spotifyStartTime}
        defaultEnd={post.spotifyEndTime}
      />
      <MediaUploader label="Fotos de la publicación" max={10} initialUrls={initialUrls} />
      <DialogFooter className="flex-shrink-0 border-t pt-4">
        <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Guardar cambios
        </Button>
      </DialogFooter>
    </form>
  );
}

function SettingsManager({ hermandad, onActionSuccess, posts }: { hermandad: JartinetHermandad; onActionSuccess: () => void; posts: SocialPost[] }) {
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const handleSave = async (updates: Partial<JartinetHermandad>) => {
        setIsSubmitting(true);
        const result = await saveHermandadSettings(hermandad.id, updates);
        if (result.success) {
            toast({ title: 'Éxito', description: result.message });
            onActionSuccess();
        } else {
            toast({ title: 'Error', description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    return (
        <div className="space-y-8">
            <UpdateColorsForm initialData={hermandad} onSave={handleSave} isSubmitting={isSubmitting} />
            <UpdateInfoForm initialData={hermandad} onSave={handleSave} isSubmitting={isSubmitting} />
            <UpdateRulesForm initialData={hermandad} onSave={handleSave} isSubmitting={isSubmitting} />
        </div>
    );
}

function UpdateColorsForm({ initialData, onSave, isSubmitting }: { initialData: JartinetHermandad; onSave: (updates: Partial<JartinetHermandad>) => void; isSubmitting: boolean }) {
    const [colorPrimario, setColorPrimario] = useState(initialData.colorPrimario);
    const [colorSecundario, setColorSecundario] = useState(initialData.colorSecundario);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave({ colorPrimario, colorSecundario });
    };

    return (
        <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Palette/> Colores Corporativos</CardTitle></CardHeader>
            <form onSubmit={handleSubmit}>
                <CardContent className="flex items-center gap-8">
                    <div className="space-y-2">
                        <Label htmlFor="colorPrimario">Color Primario</Label>
                        <Input type="color" id="colorPrimario" value={colorPrimario} onChange={(e) => setColorPrimario(e.target.value)} className="p-1 h-12" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="colorSecundario">Color Secundario</Label>
                        <Input type="color" id="colorSecundario" value={colorSecundario} onChange={(e) => setColorSecundario(e.target.value)} className="p-1 h-12" />
                    </div>
                </CardContent>
                <CardFooter className="justify-end">
                     <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Guardar Colores
                    </Button>
                </CardFooter>
            </form>
        </Card>
    );
}

function UpdateInfoForm({ initialData, onSave, isSubmitting }: { initialData: JartinetHermandad; onSave: (updates: Partial<JartinetHermandad>) => void; isSubmitting: boolean }) {
    const [info, setInfo] = useState(initialData.info || '');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave({ info });
    };

    return (
        <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><AlignLeft/> Editar Información</CardTitle></CardHeader>
            <form onSubmit={handleSubmit}>
                <CardContent>
                    <Label htmlFor="info">Edita la información pública del perfil de la hermandad.</Label>
                     <RichTextarea
                        id="info"
                        value={info}
                        onChange={(e) => setInfo(e.target.value)}
                        rows={8}
                        placeholder="Escribe aquí la historia, curiosidades o cualquier información relevante sobre la hermandad."
                    />
                    <p className="text-xs text-muted-foreground mt-1">Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link> para dar formato (negrita, cursiva...).</p>
                </CardContent>
                <CardFooter className="justify-end">
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Guardar Información
                    </Button>
                </CardFooter>
            </form>
        </Card>
    );
}

function UpdateRulesForm({ initialData, onSave, isSubmitting }: { initialData: JartinetHermandad; onSave: (updates: Partial<JartinetHermandad>) => void; isSubmitting: boolean }) {
    const [url, setUrl] = useState(initialData.libroReglasUrl || '');
    const { toast } = useToast();

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (url && !url.startsWith('http')) {
            toast({ title: 'URL inválida', description: 'Por favor, introduce una URL completa que empiece con http:// o https://', variant: 'destructive' });
            return;
        }
        onSave({ libroReglasUrl: url });
    };

    return (
        <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><BookOpen /> Libro de Reglas</CardTitle></CardHeader>
            <form onSubmit={handleSubmit}>
                <CardContent>
                    <Label htmlFor="libroReglasUrl">URL del Libro de Reglas (PDF)</Label>
                     <Input
                        id="libroReglasUrl"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://ejemplo.com/libro-de-reglas.pdf"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Pega aquí el enlace a tu libro de reglas en formato PDF.</p>
                </CardContent>
                <CardFooter className="justify-end">
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Guardar Enlace
                    </Button>
                </CardFooter>
            </form>
        </Card>
    );
}

function PinPostsManager({ hermandad, posts, onSave, isSubmitting }: { hermandad: JartinetHermandad; posts: SocialPost[]; onSave: (updates: Partial<JartinetHermandad>) => void; isSubmitting: boolean }) {
    const [pinned, setPinned] = useState(hermandad.pinnedPosts || { left: undefined, center: undefined, right: undefined });
    const [isSelectorOpen, setSelectorOpen] = useState(false);
    const [currentSlot, setCurrentSlot] = useState<'left' | 'center' | 'right' | null>(null);

    const handleSelectClick = (slot: 'left' | 'center' | 'right') => {
        setCurrentSlot(slot);
        setSelectorOpen(true);
    };

    const handlePostSelect = (postId: string | null) => {
        if (currentSlot) {
            setPinned(prev => ({ ...prev, [currentSlot]: postId }));
        }
        setSelectorOpen(false);
        setCurrentSlot(null);
    };
    
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave({ pinnedPosts: pinned });
    };

    const renderSlot = (slot: 'left' | 'center' | 'right') => {
        const postId = pinned[slot];
        const post = postId ? posts.find(p => p.id === postId) : null;
        const mediaUrl = post ? (Array.isArray(post.mediaUrl) ? post.mediaUrl[0] : post.mediaUrl) : null;
        
        return (
             <Card>
                <CardHeader>
                    <CardTitle className="capitalize text-lg">{slot}</CardTitle>
                </CardHeader>
                <CardContent className="h-24 flex items-center justify-center bg-muted/50 rounded-md">
                     {post ? (
                        <div className="flex items-center gap-2 text-sm">
                            {mediaUrl && <Image thumb src={mediaUrl} alt={post.title} width={40} height={40} className="rounded-sm aspect-square object-cover" />}
                            <span className="line-clamp-2 font-semibold">{post.title}</span>
                        </div>
                    ) : (
                        <p className="text-xs text-muted-foreground">Vacío</p>
                    )}
                </CardContent>
                <CardFooter className="pt-4">
                    <Button type="button" variant="outline" className="w-full" onClick={() => handleSelectClick(slot)}>Cambiar</Button>
                </CardFooter>
            </Card>
        );
    };

    return (
        <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Pin /> Publicaciones Fijadas</CardTitle></CardHeader>
            <form onSubmit={handleSubmit}>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {renderSlot('left')}
                    {renderSlot('center')}
                    {renderSlot('right')}
                </CardContent>
                <CardFooter className="justify-end">
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && <Loader2 className="animate-spin mr-2"/>}
                        Guardar Publicaciones Fijadas
                    </Button>
                </CardFooter>
            </form>
             <Dialog open={isSelectorOpen} onOpenChange={setSelectorOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Seleccionar Publicación</DialogTitle>
                        <DialogDescription>Elige la publicación que quieres fijar en la posición '{currentSlot}'.</DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="h-96 -mx-6 pr-2">
                        <div className="px-6 space-y-2">
                             <button type="button" onClick={() => handlePostSelect(null)} className="w-full p-3 rounded-md text-left bg-destructive/10 hover:bg-destructive/20 border border-destructive">
                                <p className="font-semibold">Quitar publicación fijada</p>
                            </button>
                            {posts.map(post => {
                                const mediaUrl = Array.isArray(post.mediaUrl) ? post.mediaUrl[0] : post.mediaUrl;
                                return (
                                    <button type="button" key={post.id} onClick={() => handlePostSelect(post.id)} className="w-full p-3 rounded-md text-left hover:bg-accent flex items-center gap-4">
                                        {mediaUrl && <Image thumb src={mediaUrl} alt={post.title} width={48} height={48} className="rounded-md aspect-square object-cover"/>}
                                        <div className="flex-grow">
                                            <p className="font-semibold line-clamp-1">{post.title}</p>
                                            <p className="text-xs text-muted-foreground line-clamp-2">{post.content}</p>
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

function CreateHighlightGroupForm({ hermandad, onFormSuccess }: { hermandad: JartinetHermandad, onFormSuccess: () => void }) {
    const { toast } = useToast();
    const formRef = useRef<HTMLFormElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [archivedStories, setArchivedStories] = useState<SocialStory[]>([]);
    const [isLoadingStories, setIsLoadingStories] = useState(true);
    const [selectedStoryIds, setSelectedStoryIds] = useState<string[]>([]);

    useEffect(() => {
        async function loadStories() {
            setIsLoadingStories(true);
            const { stories, error } = await getArchivedStories(hermandad.id);
            if (error) {
                toast({ title: "Error", description: "No se pudieron cargar las historias archivadas.", variant: "destructive" });
            } else {
                setArchivedStories(stories);
            }
            setIsLoadingStories(false);
        }
        loadStories();
    }, [hermandad.id, toast]);

    const handleStorySelection = (storyId: string, checked: boolean) => {
        setSelectedStoryIds(prev =>
            checked ? [...prev, storyId] : prev.filter(id => id !== storyId)
        );
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (selectedStoryIds.length === 0) {
            toast({ title: "Error", description: "Debes seleccionar al menos una historia.", variant: "destructive" });
            return;
        }
        setIsSubmitting(true);
        const formData = new FormData(event.currentTarget);
        
        const result = await createHighlightStoryGroup(hermandad.id, 'hermandad', formData, selectedStoryIds);
        
        if (result.success) {
            toast({ title: "Éxito", description: result.message });
            formRef.current?.reset();
            setSelectedStoryIds([]);
            onFormSuccess();
        } else {
            toast({ title: "Error", description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="title">Título de la Destacada</Label>
                <Input id="title" name="title" required />
            </div>
            <div className="space-y-2">
                <Label htmlFor="coverUrl">URL de la Portada (Imgur)</Label>
                <Input id="coverUrl" name="coverUrl" required placeholder="https://i.imgur.com/..." />
            </div>
            <div className="space-y-2">
                <Label>Selecciona Historias</Label>
                {isLoadingStories ? <Loader2 className="animate-spin" /> : (
                    <ScrollArea className="h-48 border rounded-md p-2">
                        <div className="grid grid-cols-3 gap-2">
                            {archivedStories.map(story => (
                                <div key={story.id} className="relative aspect-square">
                                    <Image thumb src={Array.isArray(story.mediaUrl) ? story.mediaUrl[0] : story.mediaUrl} alt="Historia" className="object-cover w-full h-full rounded-md" />
                                    <Checkbox
                                        className="absolute top-1 right-1 bg-background/50 border-white"
                                        onCheckedChange={(checked) => handleStorySelection(story.id, !!checked)}
                                    />
                                </div>
                            ))}
                        </div>
                         {archivedStories.length === 0 && <p className="text-center text-muted-foreground text-sm p-4">No hay historias archivadas para esta hermandad.</p>}
                    </ScrollArea>
                )}
            </div>
            <DialogFooter>
                <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
                <Button type="submit" disabled={isSubmitting || isLoadingStories}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Crear Grupo
                </Button>
            </DialogFooter>
        </form>
    );
}

function PapeletasSection({ hermandad, allHermanos, onFormSuccess }: { hermandad: JartinetHermandad; allHermanos: UserProfile[]; onFormSuccess: () => void }) {
    const { toast } = useToast();
    const [isCreatePapeletaOpen, setIsCreatePapeletaOpen] = useState(false);
    const [papeletas, setPapeletas] = useState<PapeletaDeSitio[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPapeleta, setSelectedPapeleta] = useState<PapeletaDeSitio | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const fetchPapeletas = React.useCallback(async () => {
        setIsLoading(true);
        const result = await getPapeletasByHermandad(hermandad.id);
        if (result.success && result.papeletas) setPapeletas(result.papeletas);
        else console.error("Failed to fetch papeletas:", result.error);
        setIsLoading(false);
    }, [hermandad.id]);

    useEffect(() => { fetchPapeletas(); }, [fetchPapeletas]);

    const handleDeletePapeleta = async (papeletaId: string) => {
        setDeletingId(papeletaId);
        const res = await deletePapeleta(hermandad.id, papeletaId);
        if (res.success) { toast({ title: 'Papeleta eliminada', description: res.message }); setPapeletas(prev => prev.filter(p => p.id !== papeletaId)); }
        else toast({ title: 'Error', description: res.message, variant: 'destructive' });
        setDeletingId(null);
    };

    function toDateSafeLocal(v: any): Date {
        if (!v) return new Date(0);
        if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
        const d = new Date(v);
        return isNaN(d.getTime()) ? new Date(0) : d;
    }

    return (
        <div className="space-y-4">
            <div className='flex justify-between items-center'>
                <h3 className="font-semibold text-lg flex items-center gap-2"><FileText className="h-5 w-5" /> Papeletas de Sitio</h3>
                 <Dialog open={isCreatePapeletaOpen} onOpenChange={setIsCreatePapeletaOpen}>
                    <DialogTrigger asChild><Button size="sm">Emitir Papeleta</Button></DialogTrigger>
                    <DialogContent className="max-w-4xl">
                        <DialogHeader>
                            <DialogTitle>Emitir Nueva Papeleta de Sitio</DialogTitle>
                        </DialogHeader>
                        <CreatePapeletaForm
                            hermandad={hermandad}
                            allHermanos={allHermanos}
                            onFormSuccess={onFormSuccess}
                            onClose={() => setIsCreatePapeletaOpen(false)}
                        />
                    </DialogContent>
                </Dialog>
            </div>
            {isLoading ? <Loader2 className="animate-spin" /> : (
                 papeletas && papeletas.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Hermano</TableHead>
                                <TableHead>Cargo</TableHead>
                                <TableHead>Fecha</TableHead>
                                <TableHead className="w-8"/>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {papeletas.map(p => {
                                const hermano = allHermanos.find(h => h.id === p.hermanoId);
                                return (
                                    <TableRow
                                        key={p.id}
                                        className="cursor-pointer hover:bg-primary/5 transition-colors"
                                        onClick={() => setSelectedPapeleta(p)}
                                    >
                                        <TableCell className="font-medium">{hermano?.nickname || p.hermanoId}</TableCell>
                                        <TableCell>{p.cargo}</TableCell>
                                        <TableCell className="text-muted-foreground text-xs">{format(toDateSafeLocal(p.sentAt), 'dd/MM/yyyy HH:mm', { locale: es })}</TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" disabled={deletingId === p.id}>
                                                        {deletingId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>¿Eliminar esta papeleta?</AlertDialogTitle>
                                                        <AlertDialogDescription>Se borrará también del buzón del hermano. Esta acción es permanente.</AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                        <AlertDialogAction onClick={() => handleDeletePapeleta(p.id)} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No se han emitido papeletas de sitio.</p>
                )
            )}

            {/* Dialog: ver papeleta completa */}
            <Dialog open={!!selectedPapeleta} onOpenChange={open => !open && setSelectedPapeleta(null)}>
                <DialogContent className="max-w-lg bg-card/95 backdrop-blur-xl border-white/10">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-primary" />
                            Papeleta de Sitio
                        </DialogTitle>
                    </DialogHeader>
                    {selectedPapeleta && (() => {
                        const hermano = allHermanos.find(h => h.id === selectedPapeleta.hermanoId) ?? null;
                        return (
                            <div className="flex flex-col items-center py-2">
                                {/* Mismo componente que el buzón del hermano → idénticas. */}
                                <PapeletaDeSitioDoc papeleta={selectedPapeleta} hermano={hermano} />
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function RecorridoDrawer({
    open,
    onOpenChange,
    dia,
    hermandad,
    cityMapUrl,
    carreraOficial,
    existing,
    onSubmitted,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dia: DiaSS;
    hermandad: JartinetHermandad;
    cityMapUrl: string;
    carreraOficial: RecorridoPunto[];
    existing?: RecorridoSolicitud;
    onSubmitted: () => void;
}) {
    const { toast } = useToast();
    const [draft, setDraft] = useState<RecorridoDraft>({ iglesia: null, estacion: null, ida: [], vuelta: [] });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Los días de vísperas (miércoles/jueves de pasión, viernes de dolores y
    // sábado de pasión) no tienen Carrera Oficial: la cofradía marca en su lugar
    // la iglesia donde hace estación de penitencia.
    const esVispera = dia.tipo === 'visperas';

    useEffect(() => {
        if (open) {
            setDraft({
                iglesia: existing?.iglesia || null,
                estacion: existing?.estacion || null,
                ida: existing?.ida || [],
                vuelta: existing?.vuelta || [],
            });
        }
    }, [open, existing]);

    const hasCarrera = !esVispera && carreraOficial.length >= 2;
    const carreraStart = carreraOficial[0];
    const carreraEnd = carreraOficial[carreraOficial.length - 1];

    const idaHitEndpoint = useMemo(() => {
        if (!hasCarrera || draft.ida.length === 0) return null;
        const last = draft.ida[draft.ida.length - 1];
        if (isNear(last, carreraStart)) return 'start';
        if (isNear(last, carreraEnd)) return 'end';
        return null;
    }, [draft.ida, hasCarrera, carreraStart, carreraEnd]);

    const vueltaReachedIglesia =
        draft.vuelta.length > 0 && !!draft.iglesia && isNear(draft.vuelta[draft.vuelta.length - 1], draft.iglesia);

    // Qué falta para poder enviar. Se avisa en concreto en vez de dejar el botón
    // muerto sin explicación.
    const bloqueo = (() => {
        if (!draft.iglesia) return 'Marca primero tu iglesia.';
        if (esVispera && !draft.estacion) return 'Marca la iglesia de tu estación de penitencia.';
        if (draft.ida.length < 1) return 'Dibuja el tramo de ida.';
        if (draft.vuelta.length < 1) return 'Dibuja el tramo de vuelta.';
        if (hasCarrera && !idaHitEndpoint) return 'La ida debe terminar en un extremo de la Carrera Oficial.';
        if (hasCarrera && !vueltaReachedIglesia) return 'La vuelta debe terminar en tu iglesia.';
        return null;
    })();

    const handleSubmit = async () => {
        if (!draft.iglesia || bloqueo) return;
        setIsSubmitting(true);
        const result = await submitRecorrido(dia.id, hermandad.id, {
            iglesia: draft.iglesia,
            ida: draft.ida,
            vuelta: draft.vuelta,
            estacion: draft.estacion,
        });
        if (result.success) {
            toast({ title: 'Éxito', description: result.message });
            onSubmitted();
            onOpenChange(false);
        } else {
            toast({ title: 'Error', description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[92dvh] flex-col p-0 sm:max-w-5xl">
                <DialogHeader className="px-6 pt-6">
                    <DialogTitle className="flex items-center gap-2"><Route className="h-5 w-5" /> Recorrido de {hermandad.nombre} · {dia.nombredia}</DialogTitle>
                    <DialogDescription>
                        {esVispera
                            ? 'Día de vísperas: no hay Carrera Oficial. Marca tu iglesia, la iglesia donde hacéis estación de penitencia y dibuja la ida y la vuelta.'
                            : 'Marca tu iglesia y dibuja la ida hasta enganchar con la Carrera Oficial, y la vuelta desde el otro extremo hasta tu iglesia.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
                    <RecorridoEditor
                        mapImageUrl={cityMapUrl}
                        draft={draft}
                        onChange={setDraft}
                        color={hermandad.colorPrimario || '#3b82f6'}
                        escudoUrl={hermandad.escudoUrl}
                        carreraOficial={carreraOficial}
                        esVispera={esVispera}
                    />
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-white/10 px-6 py-4">
                    <p className={cn('text-xs', bloqueo ? 'text-amber-400' : 'text-emerald-400')}>
                        {bloqueo || 'Todo listo para enviar.'}
                    </p>
                    <Button size="sm" onClick={handleSubmit} disabled={isSubmitting || !!bloqueo}>
                        {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Enviar recorrido
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/** Chip de estado compartido por el recorrido y el cuadro de horarios. */
function EstadoSolicitudBadge({ estado }: { estado?: 'pendiente' | 'aprobado' | 'rechazado' }) {
    if (!estado) return null;
    return (
        <span className={cn(
            'flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold',
            estado === 'aprobado' && 'bg-green-500/15 text-green-500',
            estado === 'pendiente' && 'bg-amber-500/15 text-amber-500',
            estado === 'rechazado' && 'bg-destructive/15 text-destructive',
        )}>
            {estado === 'aprobado' ? 'Aprobado' : estado === 'pendiente' ? 'Pendiente de confirmación' : 'Rechazado, vuelve a enviarlo'}
        </span>
    );
}

function HorarioDrawer({
    open,
    onOpenChange,
    dia,
    hermandad,
    itinerario,
    recorrido,
    cityMapUrl,
    carreraOficial,
    onSubmitted,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dia: DiaSS;
    hermandad: JartinetHermandad;
    itinerario?: ItinerarioRecord;
    recorrido?: RecorridoSolicitud;
    cityMapUrl: string | null;
    carreraOficial: RecorridoPunto[];
    onSubmitted: () => void;
}) {
    const { toast } = useToast();
    const [cuadro, setCuadro] = useState<ItinerarioRecord>(itinerario || {});
    const [estado, setEstado] = useState<EstadoCuadro>({ valido: false, error: null, puntos: 0 });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!estado.valido) return;
        setIsSubmitting(true);
        const result = await submitHorario(dia.id, hermandad.id, cuadro);
        if (result.success) {
            toast({ title: 'Éxito', description: result.message });
            onSubmitted();
            onOpenChange(false);
        } else {
            toast({ title: 'Error', description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[92dvh] flex-col p-0 sm:max-w-6xl">
                <DialogHeader className="px-6 pt-6">
                    <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5" /> Cuadro de horarios de {hermandad.nombre} · {dia.nombredia}</DialogTitle>
                    <DialogDescription>
                        Marca la hora de salida y ve añadiendo los puntos del recorrido. Cada punto va 10 minutos después
                        del anterior salvo que cambies el hueco, y todas las horas caen en la rejilla de 10 en 10.
                    </DialogDescription>
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
                    <div className="grid gap-5 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
                        {/* El recorrido aprobado, al lado, para ir mirándolo mientras se ponen las horas. */}
                        <div className="space-y-2 lg:sticky lg:top-0 lg:self-start">
                            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                <Route className="h-3.5 w-3.5 text-primary" /> Tu recorrido aprobado
                            </p>
                            {cityMapUrl && recorrido ? (
                                <RouteMapEditor
                                    mapImageUrl={cityMapUrl}
                                    reference={dia.tipo !== 'visperas' && carreraOficial.length >= 2 ? { puntos: carreraOficial, color: CARRERA_OFICIAL_COLOR } : null}
                                    referenceEndpointMarkers={dia.tipo !== 'visperas' && carreraOficial.length >= 2}
                                    segments={[
                                        { puntos: recorrido.ida, color: recorrido.color || hermandad.colorPrimario || '#3b82f6' },
                                        { puntos: recorrido.vuelta, color: darkenColor(recorrido.color || hermandad.colorPrimario || '#3b82f6'), dashed: true },
                                    ]}
                                    showSegmentPoints={false}
                                    markers={[
                                        { point: recorrido.iglesia, color: recorrido.color || hermandad.colorPrimario || '#3b82f6', ring: true, iconUrl: hermandad.escudoUrl },
                                        ...(recorrido.estacion ? [{ point: recorrido.estacion, color: '#e11d48', ring: true, cross: true }] : []),
                                    ]}
                                />
                            ) : (
                                <p className="rounded-xl border border-dashed border-white/10 p-4 text-xs text-muted-foreground">
                                    No se puede mostrar el recorrido.
                                </p>
                            )}
                            <p className="text-[0.65rem] text-muted-foreground">Línea continua = ida · discontinua = vuelta.</p>
                        </div>

                        {/* La `key` fuerza a resembrar el editor cada vez que se abre. */}
                        <CuadroHorariosEditor
                            key={open ? 'abierto' : 'cerrado'}
                            value={itinerario}
                            onChange={(nuevo, est) => { setCuadro(nuevo); setEstado(est); }}
                            esVispera={dia.tipo === 'visperas'}
                        />
                    </div>
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-white/10 px-6 py-4">
                    <p className={cn('text-xs', estado.error ? 'text-amber-400' : 'text-emerald-400')}>
                        {estado.error || `Todo listo: ${estado.puntos} puntos.`}
                    </p>
                    <Button size="sm" onClick={handleSubmit} disabled={isSubmitting || !estado.valido}>
                        {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Enviar cuadro de horarios
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function ProgramaSSTab({ hermandad }: { hermandad: JartinetHermandad }) {
    const [isLoading, setIsLoading] = useState(true);
    const [dias, setDias] = useState<DiaSS[]>([]);
    const [carreraOficial, setCarreraOficial] = useState<RecorridoPunto[]>([]);
    const [cityMapUrl, setCityMapUrl] = useState<string | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isHorarioOpen, setIsHorarioOpen] = useState(false);

    const fetchData = async () => {
        setIsLoading(true);
        const [diasResult, configResult, cityMapResult] = await Promise.all([
            getDiasSS(),
            getProgramaSSConfig(),
            getCityMapConfig(),
        ]);
        setDias(diasResult.dias || []);
        setCarreraOficial(configResult.config?.carreraOficial || []);
        setCityMapUrl(cityMapResult.config?.mapImageUrl || null);
        setIsLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [hermandad.id]);

    const diaConHermandad = useMemo(() => {
        for (const dia of dias) {
            const detalles = (Array.isArray(dia.hermandades) ? dia.hermandades : []).map(h => typeof h === 'string' ? { hermandadId: h } as DiaSSHermandadDetalles : h);
            const detalle = detalles.find(d => d.hermandadId === hermandad.id);
            if (detalle) return { dia, detalle };
        }
        return null;
    }, [dias, hermandad.id]);

    if (isLoading) {
        return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
    }

    if (!diaConHermandad) {
        return (
            <div className="text-center py-10 border-2 border-dashed rounded-2xl">
                <Route className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-muted-foreground text-sm">Tu hermandad todavía no está asignada a ningún día del programa de Semana Santa.</p>
            </div>
        );
    }

    const recorridoActual = diaConHermandad.detalle.recorrido;
    const estado = recorridoActual?.estado;
    const canEdit = !estado || estado === 'rechazado';
    const esVispera = diaConHermandad.dia.tipo === 'visperas';

    const itinerario = diaConHermandad.detalle.itinerario as ItinerarioRecord | undefined;
    const horario: HorarioSolicitud | undefined = diaConHermandad.detalle.horario;
    const tieneCuadro = Object.keys(itinerario || {}).length > 0;
    // El cuadro de horarios va sobre el recorrido: hasta que no está aprobado no
    // se puede mandar (lo comprueba también `submitHorario`).
    const recorridoAprobado = estado === 'aprobado';
    // Mismo criterio que el recorrido: mientras está pendiente o aprobado lo
    // manda el organizador del programa, que puede seguir retocándolo.
    const puedeEditarHorario = recorridoAprobado && (!horario?.estado || horario.estado === 'rechazado');

    return (
        <div className="space-y-6">
            {/* ── Recorrido ── */}
            <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-semibold text-lg flex items-center gap-2"><Route className="h-5 w-5" /> Recorrido · {diaConHermandad.dia.nombredia}</h3>
                    <EstadoSolicitudBadge estado={estado} />
                </div>

                {!cityMapUrl ? (
                    <div className="text-center py-8 border-2 border-dashed rounded-2xl">
                        <Route className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                        <p className="text-muted-foreground text-sm">El mapa de la ciudad todavía no está disponible. Vuelve más tarde.</p>
                    </div>
                ) : (
                    <>
                        {recorridoActual ? (
                            <RouteMapEditor
                                mapImageUrl={cityMapUrl}
                                // En vísperas no hay Carrera Oficial: no se pinta de referencia.
                                reference={!esVispera && carreraOficial.length >= 2 ? { puntos: carreraOficial, color: CARRERA_OFICIAL_COLOR } : null}
                                referenceEndpointMarkers={!esVispera && carreraOficial.length >= 2}
                                segments={[
                                    { puntos: recorridoActual.ida, color: recorridoActual.color || hermandad.colorPrimario || '#3b82f6' },
                                    { puntos: recorridoActual.vuelta, color: darkenColor(recorridoActual.color || hermandad.colorPrimario || '#3b82f6'), dashed: true },
                                ]}
                                showSegmentPoints={false}
                                markers={[
                                    { point: recorridoActual.iglesia, color: recorridoActual.color || hermandad.colorPrimario || '#3b82f6', ring: true, iconUrl: hermandad.escudoUrl },
                                    ...(recorridoActual.estacion ? [{ point: recorridoActual.estacion, color: '#e11d48', ring: true, cross: true }] : []),
                                ]}
                            />
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                Todavía no has enviado el recorrido de tu hermandad. Ábrelo en grande para dibujarlo con comodidad.
                            </p>
                        )}

                        {canEdit && (
                            <Button className="w-full" onClick={() => setIsDrawerOpen(true)}>
                                <Route className="h-4 w-4" /> {recorridoActual ? 'Editar y reenviar recorrido' : 'Dibujar mi recorrido'}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* ── Cuadro de horarios ── */}
            <div className="space-y-4 border-t border-white/10 pt-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-semibold text-lg flex items-center gap-2"><Clock className="h-5 w-5" /> Cuadro de horarios</h3>
                    <EstadoSolicitudBadge estado={horario?.estado} />
                </div>

                {horario?.estado === 'rechazado' && horario.motivoRechazo && (
                    <p className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                        <strong>Motivo del rechazo:</strong> {horario.motivoRechazo}
                    </p>
                )}

                {!recorridoAprobado && (
                    <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                        <Route className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" />
                        <p className="text-xs text-amber-200/90">
                            {!recorridoActual
                                ? 'Primero dibuja y envía tu recorrido: el cuadro de horarios va sobre él, así que no se puede mandar hasta que el recorrido esté aprobado.'
                                : estado === 'pendiente'
                                    ? 'Tu recorrido está pendiente de confirmación. En cuanto lo aprueben podrás enviar el cuadro de horarios.'
                                    : 'Tu recorrido fue rechazado. Vuelve a enviarlo y, cuando lo aprueben, podrás mandar el cuadro de horarios.'}
                        </p>
                    </div>
                )}

                {tieneCuadro ? (
                    <>
                        <CuadroHorariosTabla itinerario={itinerario} />
                        {horario?.estado === 'aprobado' && (
                            <p className="text-xs text-muted-foreground">
                                Aprobado. El organizador del programa puede haberlo ajustado: esto es lo que sale en el libro.
                                Si necesitas cambiarlo, pídeselo a él.
                            </p>
                        )}
                    </>
                ) : recorridoAprobado ? (
                    <p className="text-sm text-muted-foreground">
                        Todavía no has enviado el cuadro de horarios. Pon la hora de salida y ve añadiendo las calles: las horas
                        van de 10 en 10 minutos.
                    </p>
                ) : null}

                {puedeEditarHorario && (
                    <Button variant={tieneCuadro ? 'outline' : 'default'} className="w-full" onClick={() => setIsHorarioOpen(true)}>
                        <Clock className="h-4 w-4" /> {tieneCuadro ? 'Editar y reenviar cuadro de horarios' : 'Crear mi cuadro de horarios'}
                    </Button>
                )}
            </div>

            {cityMapUrl && (
                <RecorridoDrawer
                    open={isDrawerOpen}
                    onOpenChange={setIsDrawerOpen}
                    dia={diaConHermandad.dia}
                    hermandad={hermandad}
                    cityMapUrl={cityMapUrl}
                    carreraOficial={carreraOficial}
                    existing={recorridoActual}
                    onSubmitted={fetchData}
                />
            )}

            <HorarioDrawer
                open={isHorarioOpen}
                onOpenChange={setIsHorarioOpen}
                dia={diaConHermandad.dia}
                hermandad={hermandad}
                itinerario={itinerario}
                recorrido={recorridoActual}
                cityMapUrl={cityMapUrl}
                carreraOficial={carreraOficial}
                onSubmitted={fetchData}
            />
        </div>
    );
}

export function HermandadDetail({
    hermandad,
    open, 
    onOpenChange,
    requests,
    allUserProfiles,
    posts,
    stories,
    allHermandades,
    allBandas,
    allPosts,
    allOrganizaciones,
    currentUser,
    onAction,
}: { 
    hermandad: JartinetHermandad | null, 
    open: boolean, 
    onOpenChange: (open: boolean) => void,
    requests: JoinRequest[],
    allUserProfiles: Record<string, UserProfile>,
    posts: SocialPost[],
    stories: SocialStory[],
    allHermandades: JartinetHermandad[],
    allBandas: Banda[],
    allPosts: SocialPost[],
    allOrganizaciones: Organizacion[],
    currentUser: { uid: string } | null,
    onAction: () => void,
}) {
    if (!hermandad || !currentUser) {
        return null;
    }

    const [activeTab, setActiveTab] = useState<'info' | 'members' | 'requests' | 'posts' | 'stories' | 'collaborations' | 'settings' | 'contracts' | 'destacadas' | 'fijadas' | 'cartas' | 'votaciones' | 'papeletas' | 'recorrido'>('info');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
    const [isCreateStoryOpen, setIsCreateStoryOpen] = useState(false);
    const [isInviteOpen, setIsInviteOpen] = useState(false);
    const [isSendCartaOpen, setIsSendCartaOpen] = useState(false);
    const [isAnnounceOpen, setIsAnnounceOpen] = useState(false);
    const [editingPost, setEditingPost] = useState<SocialPost | null>(null);
    const [selectedPostId, setSelectedPostId] = useState('');
    const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
    const [isCreateContractOpen, setIsCreateContractOpen] = useState(false);
    const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);

    const { toast } = useToast();

    const userDocRef = useMemoFirebase(() => {
        if (!firestore || !currentUser?.uid) return null;
        return doc(firestore, 'users', currentUser.uid);
    }, [currentUser?.uid]);

    const { data: userData } = useDoc<UserProfile>(userDocRef);
    const isAdmin = userData?.role === 'admin';

    const isHermanoMayor = useMemo(() => {
        if (!hermandad || !currentUser) return false;
        return (Array.isArray(hermandad.hermanosMayores) && hermandad.hermanosMayores.includes(currentUser.uid));
    }, [hermandad, currentUser]);

    const canManage = isHermanoMayor || isAdmin;
    
    const contractsQuery = useMemoFirebase(() => {
        if (!firestore || !hermandad || !canManage) return null;
        const compositeId = `hermandad:${hermandad.id}`;
        return query(
        collection(firestore, 'contracts'),
        where('emitterId', '==', compositeId)
        );
    }, [hermandad, canManage]);
    
    const contractsReceivedQuery = useMemoFirebase(() => {
        if (!firestore || !hermandad || !canManage) return null;
        const compositeId = `hermandad:${hermandad.id}`;
        return query(
        collection(firestore, 'contracts'),
        where('recipientId', '==', compositeId)
        );
    }, [hermandad, canManage]);

    const { data: sentContracts, isLoading: isLoadingSent } = useCollection<Contract>(contractsQuery);
    const { data: receivedContracts, isLoading: isLoadingReceived } = useCollection<Contract>(contractsReceivedQuery);

    useEffect(() => {
        if (open) {
        setActiveTab('info');
        }
    }, [hermandad, open]);

    const pendingCollaborations = useMemo(() => {
        if (!hermandad || !allPosts) return [];
        return allPosts.filter(p => p.collaboratorId === `hermandad:${hermandad.id}` && p.collaborationStatus === 'pending');
    }, [allPosts, hermandad]);

    const isMember = useMemo(() => {
      if (!hermandad || !currentUser) return false;
      const hermanosList = Array.isArray(hermandad.hermanos) ? hermandad.hermanos : [];
      const hermanosMayoresList = Array.isArray(hermandad.hermanosMayores) ? hermandad.hermanosMayores : [];
      return hermanosList.includes(currentUser.uid) || hermanosMayoresList.includes(currentUser.uid);
    }, [hermandad, currentUser]);

    const userRequest = requests.find(req => req.userId === currentUser.uid);
    const hasPendingRequest = !!userRequest;

    const hexToRgba = (hex: string, alpha: number): string => {
        if (!hex?.startsWith('#')) return `rgba(0,0,0,${alpha})`;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const headerImageUrl = hermandad.foto || "https://i.imgur.com/kYkL52G.png";

    const handleGenericAction = async (action: () => Promise<any>) => {
        setIsSubmitting(true);
        try {
            const result = await action();
            if (result.success) {
                toast({ title: "Éxito", description: result.message });
                onAction(); 
            } else {
                toast({ title: "Error", description: result.error || 'Ocurrió un error inesperado', variant: "destructive" });
            }
        } catch (e: any) {
            toast({ title: "Error Inesperado", description: e.message, variant: "destructive" });
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleRequestJoin = () => handleGenericAction(() => requestToJoinHermandad(hermandad.id));
    const handleRequestAction = (actionType: 'approve' | 'reject', requestUserId: string) => handleGenericAction(() => (actionType === 'approve' ? approveJoinRequest(hermandad.id, requestUserId) : rejectJoinRequest(hermandad.id, requestUserId)));
    const handleRemoveMember = (memberId: string) => handleGenericAction(() => removeBrother(hermandad.id, memberId));
    const handleDeletePost = (postId: string) => handleGenericAction(() => deleteSocialPost(postId, hermandad.id, 'hermandad'));
    const handleAcceptCollab = (postId: string) => handleGenericAction(() => acceptCollaboration(postId));
    const handleRejectCollab = (postId: string) => handleGenericAction(() => rejectCollaboration(postId));
    const handleSignContract = (contractId: string) => handleGenericAction(() => signContract(contractId, `hermandad:${hermandad.id}`));
    const handleRejectContract = (contractId: string) => handleGenericAction(() => rejectContract(contractId, `hermandad:${hermandad.id}`));
    const handleDeleteStory = (storyId: string) => handleGenericAction(() => deleteSocialStory(storyId));
    
    const handleInviteClick = (postId: string) => {
        setSelectedPostId(postId);
        setIsInviteOpen(true);
    }
    
    const handleSaveSettings = async (updates: Partial<JartinetHermandad>) => {
        const result = await saveHermandadSettings(hermandad.id, updates);
        if (result.success) {
            toast({ title: 'Éxito', description: result.message });
            onAction();
        } else {
            toast({ title: 'Error', description: result.message, variant: 'destructive' });
        }
    };
    
    const handleDeleteHighlight = (highlightId: string) => {
      if (!hermandad) return;
      handleGenericAction(() => deleteHighlightStoryGroup(hermandad.id, 'hermandad', highlightId));
    };

    const renderFooterButton = () => {
        const style = {
            backgroundColor: hermandad.colorSecundario,
            color: hermandad.colorPrimario,
        };

        if (isMember) {
            return <Button variant="secondary" disabled><ShieldCheck className="mr-2 h-4 w-4" /> Eres Hermano</Button>;
        }
        if (hasPendingRequest) {
            return <Button variant="outline" disabled><Hourglass className="mr-2 h-4 w-4 animate-spin" /> Solicitud Pendiente</Button>;
        }
        return <Button onClick={handleRequestJoin} disabled={isSubmitting} style={style}><UserPlus className="mr-2 h-4 w-4" /> Solicitar ser Hermano</Button>;
    }
  
    const renderUser = (user: UserProfile | undefined) => {
      if (!user) return null;
      return (
        <div key={user.id} className="flex items-center justify-between gap-3 text-sm p-2 rounded-md bg-muted/50">
            <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
                <AvatarImage src={user.profileImageUrl || `https://api.dicebear.com/8.x/initials/svg?seed=${user.nickname}`} />
                <AvatarFallback>{user.nickname?.charAt(0).toUpperCase() || '?'}</AvatarFallback>
            </Avatar>
            <span>{user.nickname || user.id}</span>
            </div>
        </div>
      );
    };
    
    const hermanosMayores = (Array.isArray(hermandad.hermanosMayores) ? hermandad.hermanosMayores : []).map(uid => allUserProfiles[uid]);
    const hermanos = (Array.isArray(hermandad.hermanos) ? hermandad.hermanos : []).map(uid => allUserProfiles[uid]);
  
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-2xl w-full flex flex-col p-0 gap-0">

            {/* ── Hero Header ── */}
            <div className="relative flex-shrink-0 overflow-hidden min-h-[260px] flex flex-col justify-end">
                {/* Background layers */}
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700" style={{ backgroundImage: `url('${headerImageUrl}')` }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
                    <div className="absolute inset-0 opacity-30" style={{ background: `linear-gradient(145deg, ${hermandad.colorPrimario || '#000'} 0%, transparent 55%)` }} />
                </div>
                {/* Content */}
                <div className="relative z-10 flex flex-col items-center gap-3 text-center px-6 pb-5 pt-10">
                    {/* Shield ring */}
                    <div className="relative">
                        <div className="absolute inset-0 blur-2xl rounded-full scale-[2.2] opacity-35" style={{ backgroundColor: hermandad.colorPrimario }} />
                        <div className="relative rounded-full p-2.5 border-2 backdrop-blur-sm" style={{ borderColor: hexToRgba(hermandad.colorPrimario || '#fff', 0.45), backgroundColor: hexToRgba(hermandad.colorPrimario || '#000', 0.2) }}>
                            <Image src={hermandad.escudoUrl} alt={hermandad.nombre} width={88} height={88} className="h-[88px] w-[88px] object-contain drop-shadow-[0_4px_20px_rgba(0,0,0,0.7)]" />
                        </div>
                    </div>
                    {/* Name */}
                    <SheetHeader className="p-0 m-0">
                        <SheetTitle className="font-headline text-xl font-bold text-foreground drop-shadow-sm leading-tight">{hermandad.nombreCompleto}</SheetTitle>
                        <SheetDescription className="text-sm text-muted-foreground/70 -mt-1">{hermandad.nombre}</SheetDescription>
                    </SheetHeader>
                    {/* Badges */}
                    <div className="flex flex-wrap gap-1.5 justify-center">
                        <span className="text-[0.68rem] font-medium px-2.5 py-0.5 rounded-full border backdrop-blur-sm" style={{ borderColor: hexToRgba(hermandad.colorPrimario || '#fff', 0.5), backgroundColor: hexToRgba(hermandad.colorPrimario || '#000', 0.25) }}>
                            {hermandad.tipo}
                        </span>
                        {hermandad.dia && (
                            <span className="text-[0.68rem] font-medium px-2.5 py-0.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm">
                                {hermandad.dia}
                            </span>
                        )}
                        {hermandad.titulos?.includes('primitiva') && <span className="text-[0.68rem] font-medium px-2.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-400">Primitiva</span>}
                        {hermandad.titulos?.includes('pontificia') && <span className="text-[0.68rem] font-medium px-2.5 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/40 text-purple-400">Pontificia</span>}
                    </div>
                </div>
                {/* Two-color accent bar */}
                <div className="relative z-10 flex h-[3px] flex-shrink-0">
                    <div style={{ backgroundColor: hermandad.colorPrimario, flex: 1 }} />
                    <div style={{ backgroundColor: hermandad.colorSecundario, flex: 1 }} />
                </div>
            </div>

            {/* ── Icon tabs (compact, always visible) ── */}
            <div className="flex-shrink-0 border-b border-white/5 bg-background/60 backdrop-blur-sm">
                <TooltipProvider delayDuration={300}>
                    <div className="flex gap-0.5 px-3 py-2 flex-wrap">
                        {([
                            { id: 'info',            label: 'Info',          icon: Info },
                            { id: 'members',         label: 'Hermanos',      icon: Users },
                            ...(canManage ? [
                                { id: 'requests',        label: 'Solicitudes',   icon: Hourglass,     badge: requests.length },
                                { id: 'posts',           label: 'Publicaciones', icon: Newspaper },
                                { id: 'stories',         label: 'Historias',     icon: ImageIcon },
                                { id: 'cartas',          label: 'Cartas',        icon: Mail },
                                { id: 'votaciones',      label: 'Votaciones',    icon: Vote },
                                { id: 'papeletas',       label: 'Papeletas',     icon: FileText },
                                { id: 'recorrido',       label: 'Recorrido y cuadro de horarios', icon: Route },
                                { id: 'contracts',       label: 'Contratos',     icon: FileSignature },
                                { id: 'collaborations',  label: 'Colabs',        icon: Handshake,     badge: pendingCollaborations.length },
                                { id: 'destacadas',      label: 'Destacadas',    icon: Star },
                                { id: 'fijadas',         label: 'Fijadas',       icon: Pin },
                                { id: 'settings',        label: 'Ajustes',       icon: Settings },
                            ] : []),
                        ] as { id: string; label: string; icon: React.ElementType; badge?: number }[]).map(({ id, label, icon: Icon, badge }) => {
                            const isActive = activeTab === id;
                            return (
                                <Tooltip key={id}>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => setActiveTab(id as any)}
                                            className={cn(
                                                "relative flex items-center justify-center h-9 w-9 rounded-xl transition-all duration-200 flex-shrink-0",
                                                isActive
                                                    ? "text-white shadow-md"
                                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                                            )}
                                            style={isActive ? { backgroundColor: hermandad.colorPrimario } : {}}
                                        >
                                            <Icon className="h-4 w-4" />
                                            {badge != null && badge > 0 && (
                                                <span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                                                    {badge}
                                                </span>
                                            )}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="text-xs">
                                        {label}
                                    </TooltipContent>
                                </Tooltip>
                            );
                        })}
                    </div>
                </TooltipProvider>
            </div>

            <ScrollArea className="flex-grow relative">
                <div
                    className="absolute inset-0 opacity-[0.05]"
                    style={{
                      backgroundImage: `url(${hermandad.escudoUrl})`,
                      backgroundSize: '120px',
                      backgroundRepeat: 'repeat',
                    }}
                />
                <div className="relative z-10 p-6">
                {activeTab === 'info' && (
                    <div className="space-y-4">
                        {/* Libro de Reglas */}
                        {hermandad.libroReglasUrl && (
                            <Dialog open={isPdfViewerOpen} onOpenChange={setIsPdfViewerOpen}>
                                <DialogTrigger asChild>
                                    <button className="w-full flex items-center gap-3 rounded-2xl border p-4 transition-colors hover:bg-muted/50 text-left group" style={{ borderColor: hexToRgba(hermandad.colorPrimario || '#fff', 0.25) }}>
                                        <div className="rounded-xl p-2.5 flex-shrink-0 transition-colors" style={{ backgroundColor: hexToRgba(hermandad.colorPrimario || '#000', 0.2) }}>
                                            <BookOpen className="h-5 w-5" style={{ color: hermandad.colorPrimario }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm">Libro de Reglas</p>
                                            <p className="text-xs text-muted-foreground">Ver el reglamento oficial</p>
                                        </div>
                                        <span className="text-muted-foreground text-lg group-hover:translate-x-0.5 transition-transform">›</span>
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="w-[90vw] max-w-[90vw] h-[90vh] flex flex-col p-2">
                                    <iframe src={hermandad.libroReglasUrl} className="w-full h-full border-0" title={`Libro de Reglas de ${hermandad.nombreCompleto}`} />
                                </DialogContent>
                            </Dialog>
                        )}

                        {/* Stat pills */}
                        <div className="grid grid-cols-3 gap-2.5">
                            {[
                                { label: 'Fundación', value: hermandad.fundacion, icon: Calendar },
                                { label: 'Hermanos', value: hermanos.length, icon: Users },
                                { label: 'HH. Mayores', value: hermanosMayores.filter(Boolean).length, icon: Crown },
                            ].map(({ label, value, icon: Icon }) => (
                                <div key={label} className="rounded-2xl border p-3 text-center" style={{ borderColor: hexToRgba(hermandad.colorPrimario || '#fff', 0.18), background: hexToRgba(hermandad.colorPrimario || '#000', 0.1) }}>
                                    <Icon className="h-4 w-4 mx-auto mb-1.5 opacity-60" />
                                    <p className="text-lg font-bold leading-none">{value}</p>
                                    <p className="text-[0.65rem] text-muted-foreground mt-1">{label}</p>
                                </div>
                            ))}
                        </div>

                        {/* Info rows */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-3 rounded-2xl bg-muted/40 border border-white/5 p-3.5">
                                <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">Titulares</p>
                                    <p className="text-sm font-medium leading-snug">{hermandad.titulares}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 rounded-2xl bg-muted/40 border border-white/5 p-3.5">
                                <Church className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <div>
                                    <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">Sede Canónica</p>
                                    <p className="text-sm font-medium">{hermandad.sede}</p>
                                </div>
                            </div>
                            {hermanosMayores.filter(Boolean).length > 0 && (
                                <div className="flex items-start gap-3 rounded-2xl bg-muted/40 border border-white/5 p-3.5">
                                    <Crown className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wider font-semibold mb-2">Hermanos Mayores</p>
                                        <div className="flex flex-wrap gap-2">
                                            {hermanosMayores.filter(Boolean).map(user => user && (
                                                <div key={user.id} className="flex items-center gap-1.5 bg-background/60 rounded-full pl-0.5 pr-3 py-0.5 border border-white/10">
                                                    <Avatar className="h-5 w-5">
                                                        <AvatarImage src={user.profileImageUrl || `https://api.dicebear.com/8.x/initials/svg?seed=${user.nickname}`} />
                                                        <AvatarFallback className="text-[0.6rem]">{user.nickname?.charAt(0) || '?'}</AvatarFallback>
                                                    </Avatar>
                                                    <span className="text-xs font-medium">{user.nickname || user.id}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {hermandad.info && (
                            <>
                                <div className="h-px w-full" style={{ background: `linear-gradient(90deg, ${hermandad.colorPrimario}, ${hermandad.colorSecundario})` }} />
                                <div className="rounded-2xl bg-muted/30 border border-white/5 p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <AlignLeft className="h-3.5 w-3.5 text-muted-foreground" />
                                        <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wider font-semibold">Información</p>
                                    </div>
                                    <div className="prose prose-sm dark:prose-invert text-muted-foreground max-w-none">
                                        <ReactMarkdown>{hermandad.info}</ReactMarkdown>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                {activeTab === 'members' && (
                    <div className="space-y-4">
                        <p className="text-[0.7rem] text-muted-foreground uppercase tracking-wider font-semibold flex items-center gap-2">
                            <Users className="h-3.5 w-3.5" /> Hermanos · {hermanos.length}
                        </p>
                        {hermanos.length > 0 ? (
                            <div className="grid grid-cols-2 gap-2">
                                {hermanos.map(user => user && (
                                    <div key={user.id} className="flex items-center gap-2.5 rounded-2xl bg-muted/40 border border-white/5 p-2.5">
                                        <Avatar className="h-9 w-9 flex-shrink-0">
                                            <AvatarImage src={user.profileImageUrl || `https://api.dicebear.com/8.x/initials/svg?seed=${user.nickname}`} />
                                            <AvatarFallback className="text-xs">{user.nickname?.charAt(0).toUpperCase() || '?'}</AvatarFallback>
                                        </Avatar>
                                        <p className="text-sm font-medium truncate flex-1">{user.nickname || user.id}</p>
                                        {canManage && (
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button size="icon" variant="ghost" className="h-7 w-7 flex-shrink-0 text-destructive/50 hover:text-destructive hover:bg-destructive/10" disabled={isSubmitting}>
                                                        <X className="h-3.5 w-3.5" />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>¿Expulsar a {user.nickname}?</AlertDialogTitle>
                                                        <AlertDialogDescription>Esta acción eliminará al hermano de la hermandad.</AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                        <AlertDialogAction onClick={() => handleRemoveMember(user.id)} className="bg-destructive hover:bg-destructive/80">Confirmar</AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-10 border-2 border-dashed rounded-2xl">
                                <Users className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                                <p className="text-muted-foreground text-sm">Esta hermandad todavía no tiene hermanos registrados.</p>
                            </div>
                        )}
                    </div>
                )}
                {activeTab === 'papeletas' && canManage && (
                    <PapeletasSection hermandad={hermandad} allHermanos={[...hermanos, ...hermanosMayores].filter(Boolean) as UserProfile[]} onFormSuccess={onAction} />
                )}
                {activeTab === 'recorrido' && canManage && (
                    <ProgramaSSTab hermandad={hermandad} />
                )}
                {activeTab === 'requests' && canManage && (
                    <div className="space-y-4">
                        <h3 className="font-semibold text-lg flex items-center gap-2"><Hourglass className="h-5 w-5" /> Solicitudes Pendientes ({requests.length})</h3>
                        {requests.length > 0 ? (
                            requests.map(req => {
                                const requestUser = allUserProfiles[req.userId];
                                return (
                                <div key={req.id} className="flex items-center justify-between gap-3 text-sm p-2 rounded-md bg-muted/50">
                                    <div className="flex items-center gap-3">
                                        <Avatar className="h-8 w-8">
                                            <AvatarImage src={requestUser?.profileImageUrl || `https://api.dicebear.com/8.x/initials/svg?seed=${requestUser?.nickname}`} />
                                            <AvatarFallback>{requestUser?.nickname?.charAt(0).toUpperCase() || '?'}</AvatarFallback>
                                        </Avatar>
                                        <span>{requestUser?.nickname || req.userId}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleRequestAction('reject', req.userId)} disabled={isSubmitting}><X className="h-4 w-4" /></Button>
                                        <Button size="icon" variant="ghost" className="h-8 w-8 text-green-500" onClick={() => handleRequestAction('approve', req.userId)} disabled={isSubmitting}><Check className="h-4 w-4" /></Button>
                                    </div>
                                </div>
                                )})
                        ) : (
                            <p className="text-muted-foreground text-sm">No hay solicitudes pendientes.</p>
                        )}
                    </div>
                )}
                {activeTab === 'posts' && canManage && (
                    <div className="space-y-4">
                        <div className='flex justify-between items-center'>
                        <h3 className="font-semibold text-lg flex items-center gap-2"><Newspaper className="h-5 w-5" /> Publicaciones ({posts.length})</h3>
                        <Dialog open={isCreatePostOpen} onOpenChange={setIsCreatePostOpen}>
                            <DialogTrigger asChild>
                                <Button size="sm">Crear Publicación</Button>
                            </DialogTrigger>
                             <DialogContent className="sm:max-w-2xl max-h-[90dvh] flex flex-col p-0">
                                <DialogHeader className="px-6 pt-6">
                                <DialogTitle>Nueva Publicación</DialogTitle>
                                <DialogDescription>Crea una nueva publicación para el muro de la hermandad.</DialogDescription>
                                </DialogHeader>
                                <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
                                  <CreatePostForm hermandadId={hermandad.id} onFormSuccess={() => { setIsCreatePostOpen(false); onAction(); }} allHermandades={allHermandades} allBandas={allBandas} allOrganizaciones={allOrganizaciones} />
                                </div>
                            </DialogContent>
                        </Dialog>
                        </div>
                        {posts.length > 0 ? (
                        posts.sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(post => (
                            <div key={post.id} className="flex items-start gap-4 p-3 rounded-md border bg-muted/50">
                            <Image
                                thumb
                                src={Array.isArray(post.mediaUrl) && post.mediaUrl.length > 0 ? post.mediaUrl[0] : typeof post.mediaUrl === 'string' ? post.mediaUrl : post.authorShieldUrl}
                                alt={post.title}
                                width={80}
                                height={80}
                                className="rounded-md aspect-square object-cover"
                                />
                            <div className="flex-grow">
                                <p className="font-semibold flex items-center gap-2">
                                {post.title}
                                {post.collaborationStatus === 'pending' && <Hourglass className="h-3 w-3 text-muted-foreground animate-spin" />}
                                </p>
                                <div className="text-xs text-muted-foreground line-clamp-2 prose prose-sm dark:prose-invert max-w-none"><ReactMarkdown>{post.content}</ReactMarkdown></div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {post.createdAt ? new Date(post.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric'}) : 'Fecha no disponible'}
                                </p>
                            </div>
                            <div className="flex flex-col gap-1">
                                    <Dialog open={isInviteOpen && selectedPostId === post.id} onOpenChange={(open) => { if (!open) setSelectedPostId(''); setIsInviteOpen(open); }}>
                                        <DialogTrigger asChild>
                                            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!!post.collaboratorId} onClick={() => handleInviteClick(post.id)}>
                                                <LinkIcon className="h-4 w-4" />
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Invitar a Colaborar</DialogTitle>
                                                <DialogDescription>
                                                    Invita a otra hermandad o banda a aparecer en esta publicación. La publicación aparecerá en ambos perfiles.
                                                </DialogDescription>
                                            </DialogHeader>
                                            <InviteCollaborationForm 
                                                postId={post.id} 
                                                allHermandades={allHermandades}
                                                allBandas={allBandas}
                                                allOrganizaciones={allOrganizaciones}
                                                currentAuthor={{type: 'hermandad', id: hermandad.id}}
                                                onFormSuccess={onAction}
                                            />
                                        </DialogContent>
                                    </Dialog>
                                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingPost(post)} title="Editar publicación">
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" disabled={isSubmitting}><Trash2 className="h-4 w-4"/></Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>¿Seguro que quieres eliminar esta publicación?</AlertDialogTitle>
                                                <AlertDialogDescription>Esta acción es permanente y no se puede deshacer.</AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                <AlertDialogAction onClick={() => handleDeletePost(post.id)} className="bg-destructive hover:bg-destructive/80">
                                                Confirmar
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                            </div>
                            </div>
                        ))
                        ) : (
                        <p className="text-muted-foreground text-sm py-4">Esta hermandad aún no tiene publicaciones.</p>
                        )}
                        <Dialog open={!!editingPost} onOpenChange={(open) => { if (!open) setEditingPost(null); }}>
                            <DialogContent className="sm:max-w-2xl max-h-[90dvh] flex flex-col p-0">
                                <DialogHeader className="px-6 pt-6">
                                    <DialogTitle>Editar Publicación</DialogTitle>
                                    <DialogDescription>Modifica el contenido, las fotos y la música de la publicación.</DialogDescription>
                                </DialogHeader>
                                <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
                                  {editingPost && <EditPostForm post={editingPost} onFormSuccess={() => { setEditingPost(null); onAction(); }} />}
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                )}
                {activeTab === 'stories' && canManage && (
                    <div className="space-y-4">
                        <div className='flex justify-between items-center'>
                        <h3 className="font-semibold text-lg flex items-center gap-2"><ImageIcon className="h-5 w-5" /> Historias Activas ({stories.length})</h3>
                        <Dialog open={isCreateStoryOpen} onOpenChange={setIsCreateStoryOpen}>
                            <DialogTrigger asChild>
                            <Button size="sm">Añadir Historia</Button>
                            </DialogTrigger>
                            <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Nueva Historia</DialogTitle>
                                <DialogDescription>Publica una imagen o vídeo que será visible durante 24 horas.</DialogDescription>
                            </DialogHeader>
                            <CreateStoryForm entityId={hermandad.id} entityType="hermandad" onFormSuccess={() => { setIsCreateStoryOpen(false); onAction(); }} />
                            </DialogContent>
                        </Dialog>
                        </div>
                        {stories.length > 0 ? (
                            <div className="grid grid-cols-3 gap-2">
                                {stories.map(story => {
                                    let thumbnailUrl = "https://i.imgur.com/placeholder.png"; // Fallback image
                                    if (story.mediaType === 'image' && story.mediaUrl) {
                                        thumbnailUrl = Array.isArray(story.mediaUrl) ? story.mediaUrl[0] : story.mediaUrl;
                                    } else if (story.mediaType === 'video' && typeof story.mediaUrl === 'string') {
                                        const videoId = story.mediaUrl.split('/').pop()?.split('.')[0];
                                        if (videoId) {
                                            thumbnailUrl = `https://i.imgur.com/${videoId}h.jpg`; // Use 'h' for huge thumbnail
                                        }
                                    }
                                    
                                    return (
                                    <div key={story.id} className="relative aspect-[9/16] rounded-md overflow-hidden group">
                                        <Image src={thumbnailUrl} alt="Imagen de historia" fill className="object-cover" />
                                        <div className="absolute inset-0 bg-black/50 flex items-end p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <p className="text-white text-xs line-clamp-2">Expira en {Math.round((new Date(story.expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60))}h</p>
                                        </div>
                                    </div>
                                )})}
                            </div>
                        ) : (
                            <p className="text-muted-foreground text-sm py-4">Esta hermandad no tiene historias activas.</p>
                        )}
                    </div>
                )}
                {activeTab === 'cartas' && canManage && (
                    <div className="space-y-4">
                        <div className='flex justify-between items-center'>
                            <h3 className="font-semibold text-lg flex items-center gap-2"><Mail className="h-5 w-5" /> Envío de Cartas</h3>
                            <Dialog open={isSendCartaOpen} onOpenChange={setIsSendCartaOpen}>
                                <DialogTrigger asChild><Button size="sm">Nueva Carta</Button></DialogTrigger>
                                <DialogContent className="sm:max-w-xl">
                                    <DialogHeader>
                                        <DialogTitle>Redactar Nueva Carta</DialogTitle>
                                        <DialogDescription>La carta se enviará al buzón de todos los hermanos de la hermandad.</DialogDescription>
                                    </DialogHeader>
                                    <SendCartaForm hermandadId={hermandad.id} onFormSuccess={() => { setIsSendCartaOpen(false); onAction(); }}/>
                                </DialogContent>
                            </Dialog>
                        </div>
                        <div className="text-center py-8 border-2 border-dashed rounded-lg">
                            <p className="text-muted-foreground text-sm">Desde aquí puedes enviar comunicaciones oficiales a todos los miembros de tu hermandad.</p>
                        </div>

                        {/* Anuncio al servidor principal de Minecraft */}
                        <div className="flex justify-between items-center pt-2">
                            <h3 className="font-semibold text-lg flex items-center gap-2"><Megaphone className="h-5 w-5" /> Anuncio al Servidor</h3>
                            <Dialog open={isAnnounceOpen} onOpenChange={setIsAnnounceOpen}>
                                <DialogTrigger asChild><Button size="sm" variant="secondary">Nuevo anuncio</Button></DialogTrigger>
                                <DialogContent className="sm:max-w-lg">
                                    <DialogHeader>
                                        <DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5" /> Anuncio al servidor principal</DialogTitle>
                                        <DialogDescription>Se mostrará en pantalla (bossbar) a todos los jugadores conectados durante 1 minuto.</DialogDescription>
                                    </DialogHeader>
                                    <ServerAnnouncementForm hermandadId={hermandad.id} onFormSuccess={() => setIsAnnounceOpen(false)} />
                                </DialogContent>
                            </Dialog>
                        </div>
                        <div className="text-center py-6 border-2 border-dashed rounded-lg">
                            <p className="text-muted-foreground text-sm">Lanza un aviso instantáneo a quienes estén jugando ahora mismo en el servidor de Minecraft.</p>
                        </div>
                    </div>
                )}
                {activeTab === 'votaciones' && canManage && (
                    <VotacionesSection hermandad={hermandad} hermanos={[...hermanos, ...hermanosMayores].filter(Boolean) as UserProfile[]} />
                )}
                {activeTab === 'collaborations' && canManage && (
                <div className="space-y-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2"><Handshake className="h-5 w-5" /> Invitaciones Pendientes ({pendingCollaborations.length})</h3>
                    {pendingCollaborations.length > 0 ? (
                    pendingCollaborations.map(post => (
                        <div key={post.id} className="flex items-center justify-between gap-3 text-sm p-3 rounded-md bg-muted/50 border">
                            <div className="flex items-center gap-3 flex-grow overflow-hidden">
                                <Avatar className="h-10 w-10">
                                    <AvatarImage src={post.authorShieldUrl} />
                                    <AvatarFallback>{post.authorName?.substring(0, 2)}</AvatarFallback>
                                </Avatar>
                                <div className='truncate'>
                                    <p className="font-semibold truncate">{post.authorName}</p>
                                    <p className="text-xs text-muted-foreground truncate">Te ha invitado a colaborar en: "{post.title}"</p>
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleRejectCollab(post.id)} disabled={isSubmitting}><X className="h-4 w-4" /></Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-green-500" onClick={() => handleAcceptCollab(post.id)} disabled={isSubmitting}><Check className="h-4 w-4" /></Button>
                            </div>
                        </div>
                    ))
                    ) : (
                    <p className="text-muted-foreground text-sm">No tienes invitaciones de colaboración pendientes.</p>
                    )}
                </div>
                )}
                {activeTab === 'settings' && canManage && (
                    <div className="space-y-4">
                        <h3 className="font-semibold text-lg flex items-center gap-2"><Settings className="h-5 w-5" /> Ajustes de la Hermandad</h3>
                        <SettingsManager hermandad={hermandad} onActionSuccess={onAction} posts={posts} />
                    </div>
                )}
                {activeTab === 'fijadas' && canManage && (
                    <div className="space-y-4">
                        <PinPostsManager hermandad={hermandad} posts={posts} onSave={handleSaveSettings} isSubmitting={isSubmitting}/>
                    </div>
                )}
                {activeTab === 'destacadas' && canManage && (
                    <div className="space-y-4">
                       <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-lg flex items-center gap-2"><Star className="h-5 w-5" /> Historias Destacadas</h3>
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button size="sm">Crear Grupo</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Crear Grupo de Historias Destacadas</DialogTitle>
                                    <DialogDescription>Agrupa tus mejores momentos.</DialogDescription>
                                </DialogHeader>
                                <CreateHighlightGroupForm hermandad={hermandad} onFormSuccess={onAction} />
                            </DialogContent>
                        </Dialog>
                    </div>
                     {(hermandad.highlightGroups || []).length > 0 ? (
                        hermandad.highlightGroups?.map(group => (
                            <div key={group.id} className="flex items-center justify-between p-2 border rounded-md">
                                <div className="flex items-center gap-3">
                                    <Avatar>
                                        <AvatarImage src={group.coverUrl} />
                                    </Avatar>
                                    <p className="font-semibold">{group.name} ({group.storyIds.length} historias)</p>
                                </div>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-destructive h-8 w-8">
                                      <Trash2 className="h-4 w-4"/>
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>¿Eliminar este grupo de destacadas?</AlertDialogTitle>
                                      <AlertDialogDescription>Esta acción es permanente.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeleteHighlight(group.id)} className="bg-destructive hover:bg-destructive/80">
                                        Confirmar
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-muted-foreground">Crea y gestiona los grupos de historias destacadas que aparecerán en tu perfil.</p>
                    )}
                    </div>
                )}
                {activeTab === 'contracts' && canManage && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h3 className="font-semibold text-lg flex items-center gap-2"><FileSignature className="h-5 w-5" /> Contratos</h3>
                            <Dialog open={isCreateContractOpen} onOpenChange={setIsCreateContractOpen}>
                                <DialogTrigger asChild><Button size="sm">Crear Contrato</Button></DialogTrigger>
                                <DialogContent className="sm:max-w-2xl">
                                    <DialogHeader>
                                        <DialogTitle>Emitir Nuevo Contrato</DialogTitle>
                                        <DialogDescription>
                                            Redacta un contrato para enviarlo a cualquier hermandad, banda u organización.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <CreateContractForm
                                    emitter={{id: hermandad.id, type: 'hermandad', name: hermandad.nombreCompleto}}
                                    allHermandades={allHermandades}
                                    allBandas={allBandas}
                                    allOrganizaciones={allOrganizaciones}
                                    onFormSuccess={() => { setIsCreateContractOpen(false); onAction(); }}
                                    />
                                </DialogContent>
                            </Dialog>
                        </div>
                        {isLoadingSent || isLoadingReceived ? <Loader2 className="h-6 w-6 animate-spin" /> : (
                            <>
                                <div>
                                    <h4 className="font-medium text-md mb-2">Contratos Recibidos</h4>
                                    <div className="space-y-2">
                                        {(receivedContracts || []).map(contract => (
                                        <div key={contract.id} className="p-2 border rounded-md hover:bg-muted/50">
                                            <button className="w-full text-left" onClick={() => setSelectedContract(contract)}>
                                                <p className="font-semibold">{contract.concept}</p>
                                                <p className="text-xs text-muted-foreground">De: {contract.emitterName}</p>
                                                <p className={`text-xs font-bold ${contract.status === 'signed' ? 'text-green-500' : 'text-yellow-500'}`}>
                                                    {contract.status.toUpperCase()}
                                                </p>
                                            </button>
                                        </div>
                                        ))}
                                        {(receivedContracts || []).length === 0 && <p className="text-xs text-muted-foreground">No has recibido contratos.</p>}
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-medium text-md mb-2">Contratos Enviados</h4>
                                    <div className="space-y-2">
                                        {(sentContracts || []).map(contract => (
                                        <div key={contract.id} className="p-2 border rounded-md hover:bg-muted/50">
                                            <button className="w-full text-left" onClick={() => setSelectedContract(contract)}>
                                                <p className="font-semibold">{contract.concept}</p>
                                                <p className="text-xs text-muted-foreground">Para: {contract.recipientName}</p>
                                                <p className={`text-xs font-bold ${contract.status === 'signed' ? 'text-green-500' : 'text-yellow-500'}`}>
                                                    {contract.status.toUpperCase()}
                                                </p>
                                            </button>
                                        </div>
                                        ))}
                                        {(sentContracts || []).length === 0 && <p className="text-xs text-muted-foreground">No has enviado contratos.</p>}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                </div>
            </ScrollArea>
            
            <SheetFooter className="p-6 border-t flex-shrink-0">
            {renderFooterButton()}
            </SheetFooter>

            {selectedContract && (
                <Dialog open={!!selectedContract} onOpenChange={(open) => !open && setSelectedContract(null)}>
                    <DialogContent className="w-full max-w-md h-auto max-h-[90vh] p-0">
                        <ContractCard contract={selectedContract} perspective="recipient" isDetailView />
                        {selectedContract.recipientId === `hermandad:${hermandad.id}` && selectedContract.status === 'pending' && (
                            <DialogFooter className="border-t p-4">
                            <Button variant="destructive" onClick={() => { handleRejectContract(selectedContract.id); setSelectedContract(null); }} disabled={isSubmitting}>Rechazar</Button>
                            <Button onClick={() => { handleSignContract(selectedContract.id); setSelectedContract(null); }} disabled={isSubmitting}>Firmar Contrato</Button>
                            </DialogFooter>
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </SheetContent>
        </Sheet>
    );
}
